/**
 * WebSocket工具类
 * 提供WebSocket连接管理、自动重连、心跳检测等功能
 */
export default class WebSocketUtil {
  constructor(options) {
    this.socket = null;
    this.url = options.url;
    this.protocols = options.protocols;
    this.heartbeatInterval = options.heartbeatInterval !== undefined ? options.heartbeatInterval : 30000;
    this.reconnectInterval = options.reconnectInterval !== undefined ? options.reconnectInterval : 5000;
    this.maxReconnectAttempts = options.maxReconnectAttempts !== undefined ? options.maxReconnectAttempts : 10;
    this.reconnectAttempts = 0;
    this.heartbeatTimer = null;
    this.reconnectTimer = null;
    this.eventListeners = {};
    this.isManualClose = false;

    this.connect();
  }

  /**
   * 建立WebSocket连接
   */
  connect() {
    this.isManualClose = false;
    try {
      this.socket = this.protocols
        ? new WebSocket(this.url, this.protocols)
        : new WebSocket(this.url);

      this.setupEventListeners();
    } catch (error) {
      this.handleError(error);
      this.scheduleReconnect();
    }
  }

  /**
   * 设置WebSocket事件监听器
   */
  setupEventListeners() {
    if (!this.socket) return;

    this.socket.onopen = (event) => {
      this.reconnectAttempts = 0;
      this.startHeartbeat();
      this.emit('open', event);
    };

    this.socket.onmessage = (event) => {
      this.emit('message', event);
    };

    this.socket.onclose = (event) => {
      this.stopHeartbeat();
      if (!this.isManualClose) {
        this.emit('close', event);
        this.scheduleReconnect();
      }
    };

    this.socket.onerror = (event) => {
      this.handleError(event);
      this.emit('error', event);
    };
  }

  /**
   * 启动心跳检测
   */
  startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.send('ping');
      } else {
        this.stopHeartbeat();
        if (!this.isManualClose) {
          this.scheduleReconnect();
        }
      }
    }, this.heartbeatInterval);
  }

  /**
   * 停止心跳检测
   */
  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * 安排重连
   */
  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.emit('reconnect_failed');
      return;
    }

    this.reconnectAttempts++;
    this.reconnectTimer = setTimeout(() => {
      this.emit('reconnect_attempt', this.reconnectAttempts);
      this.connect();
    }, this.reconnectInterval * this.reconnectAttempts);
  }

  /**
   * 错误处理
   */
  handleError(error) {
    console.error('[WebSocket Error]', error);
  }

  /**
   * 触发事件
   */
  emit(event, data) {
    if (this.eventListeners[event]) {
      this.eventListeners[event].forEach(callback => callback(data));
    }
  }

  /**
   * 发送数据
   */
  send(data) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(data);
    } else {
      throw new Error('WebSocket is not open: ' + this.getReadyStateDescription());
    }
  }

  /**
   * 添加事件监听器
   */
  on(event, callback) {
    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    this.eventListeners[event].push(callback);
  }

  /**
   * 移除事件监听器
   */
  off(event, callback) {
    if (!this.eventListeners[event]) return;

    if (callback) {
      this.eventListeners[event] = this.eventListeners[event].filter(cb => cb !== callback);
    } else {
      delete this.eventListeners[event];
    }
  }

  /**
   * 关闭WebSocket连接
   */
  close(code, reason) {
    this.isManualClose = true;
    this.stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      this.socket.close(code, reason);
      this.socket = null;
    }
  }

  /**
   * 获取当前连接状态码
   */
  getReadyState() {
    return this.socket?.readyState ?? WebSocket.CLOSED;
  }

  /**
   * 获取当前连接状态描述
   */
  getReadyStateDescription() {
    const readyState = this.getReadyState();
    switch (readyState) {
      case WebSocket.CONNECTING:
        return 'CONNECTING';
      case WebSocket.OPEN:
        return 'OPEN';
      case WebSocket.CLOSING:
        return 'CLOSING';
      case WebSocket.CLOSED:
        return 'CLOSED';
      default:
        return 'UNKNOWN';
    }
  }

  /**
   * 检查连接是否已建立
   */
  isConnected() {
    return this.getReadyState() === WebSocket.OPEN;
  }
}
